import { FormEvent, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

// Step 1 of checkout: create an account or log in without leaving the page.
// Posts to /checkout/register or /checkout/login, which start a session and
// round-trip back to /buy (including `extraParams` — coupon, seats, ldt — so
// the payment step resumes exactly where the visitor left off). The
// register/login mode is controlled by the parent because the progress
// header's step-1 label depends on it.

type ExtraParams = Record<string, string | number>

export function CheckoutAccountStep({
  mode,
  onModeChange,
  registerIntro,
  loginIntro,
  extraParams,
}: {
  mode: "register" | "login"
  onModeChange: (mode: "register" | "login") => void
  registerIntro: string
  loginIntro: string
  extraParams: ExtraParams
}) {
  const { props } = usePage<PageProps>()
  const register = mode === "register"
  const form = useForm<{ email: string; password: string } & ExtraParams>({
    email: "",
    password: "",
    ...extraParams,
  })

  const extraKey = JSON.stringify(extraParams)
  useEffect(() => {
    Object.entries(extraParams).forEach(([key, value]) => form.setData(key, value))
  }, [extraKey]) // eslint-disable-line

  function submit(e: FormEvent) {
    e.preventDefault()
    form.post(register ? "/checkout/register" : "/checkout/login", { preserveScroll: true })
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-ink-muted">{register ? registerIntro : loginIntro}</p>
      {props.errors?.base && <p className="text-sm text-danger-display">{props.errors.base}</p>}
      <div className="space-y-2">
        <label htmlFor="checkout-email">Email</label>
        <Input
          id="checkout-email"
          type="email"
          autoComplete="email"
          autoFocus
          required
          value={form.data.email}
          aria-invalid={!!form.errors.email}
          onChange={(e) => form.setData("email", e.target.value)}
        />
        {form.errors.email && <p className="text-xs text-danger-display">{form.errors.email}</p>}
      </div>
      <div className="space-y-2">
        <label htmlFor="checkout-password">{register ? "Choose a password" : "Password"}</label>
        <Input
          id="checkout-password"
          type="password"
          autoComplete={register ? "new-password" : "current-password"}
          required
          minLength={register ? 7 : undefined}
          value={form.data.password}
          aria-invalid={!!form.errors.password}
          onChange={(e) => form.setData("password", e.target.value)}
        />
        {form.errors.password && (
          <p className="text-xs text-danger-display">{form.errors.password}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4 pt-1">
        <Button type="submit" disabled={form.processing}>
          {form.processing
            ? "One moment…"
            : register
              ? "Create account & continue"
              : "Log in & continue"}
        </Button>
        <button
          type="button"
          onClick={() => onModeChange(register ? "login" : "register")}
          className="text-sm text-ink-muted hover:underline"
        >
          {register ? "Already have an account? Log in" : "New here? Create an account"}
        </button>
      </div>
    </form>
  )
}
